import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, signal } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { IConditionalFormattingComponent } from "../../rule-engine/directives/condition-formatting.directive";
import { zAppDevComponentStatus } from "../component-status";
import { zAppDevComponentSize } from '@framework/components/component-size';
import { transformStyleToObject } from "../../common/utils";

@Component({
    template: ``,
    standalone: true,
})
export abstract class zAppDevBaseComponent implements OnInit, OnDestroy, IConditionalFormattingComponent {

  constructor(protected elementRef: ElementRef) {
    this._elementRef = elementRef;
  }

  @Input() class: string = '';

  private _required = signal<boolean>(false);
  @Input()
  get required(): boolean { return this._required(); }
  set required(v: boolean) { this._required.set(v ?? false); }

  private _readonly = signal<boolean>(false);
  @Input()
  get readonly(): boolean { return this._readonly(); }
  set readonly(v: boolean) { this._readonly.set(v ?? false); }

  private _disabled = signal<boolean>(false);
  @Input()
  get disabled(): boolean { return this._disabled(); }
  set disabled(v: boolean) { this._disabled.set(v ?? false); }

  @Input() status?: zAppDevComponentStatus;

  @Input() size?: zAppDevComponentSize;

  @Input() variation: string = 'Standard';

  _elementRef: ElementRef;

  protected options: any;

  // Signal-backed so the directive's async mutations (inside Promise.then) trigger OnPush CD
  private _hidden = signal<boolean>(false);
  get hidden(): boolean { return this._hidden(); }
  set hidden(v: boolean) { this._hidden.set(v); }

  private _collapsed = signal<boolean>(false);
  get collapsed(): boolean { return this._collapsed(); }
  set collapsed(v: boolean) { this._collapsed.set(v); }

  noPermission?: boolean;

  statusSubject: BehaviorSubject<zAppDevComponentStatus> = new BehaviorSubject(null);

  hostClassesArray = signal<string[]>([]);
  styleClassesArray = signal<string[]>([]);

  @Input() style: string = '';

  get _style(): any {
    return transformStyleToObject(this.style);
  }

  @HostBinding('style')
  get hostStyle(): any {
    return this._style;
  }

  @HostBinding('class')
  get hostClasses(): string {
    return this.hostClassesArray().join(' ');
  }
  set hostClasses(classes: string) {
    this.hostClassesArray.set(classes.split(' '));
  }

  get styleClasses(): string {
    return this.styleClassesArray().join(' ');
  }
  set styleClasses(classes: string) {
    this.styleClassesArray.set(classes.split(' '));
  }

  @HostBinding('class.required')
  get requiredField() {
    return this.required === true;
  }

  isList: boolean = false;

  protected destroy$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.hostClassesArray.set(this.cssHostClasses());
    this.styleClassesArray.set(this.cssStyleClasses());
  }

  statusClass() {
    return (this.options ?? [])[this.variation]?.Classes.Roles[this.status] ?? '';
  }

  focus() {
    console.error('Focus method not implemented in base component');
  }

  sizeClass() {
    return (this.options ?? [])[this.variation]?.Classes.Roles[this.size] ?? '';
  }

  rootClass() {
    return (this.options ?? [])[this.variation]?.Classes?.Global ?? '';
  }

  setStatus(status: zAppDevComponentStatus) {
    this.status = status;
    this.statusSubject.next(this.status);
    this.hostClassesArray.set(this.cssHostClasses());
  }

  applyCssClasses(classes: string) {
    this.styleClassesArray.update(arr => [...arr, ...classes.split(' ')]);
  }

  removeCssClasses(classes: string) {
    const classesArr = classes.split(' ');
    this.styleClassesArray.update(arr => arr.filter(c => !classesArr.some(cls => cls === c)));
  }

  cssStyleClasses(): string[] {
    return [
      this.class
    ];
  }

  cssHostClasses(): string[] {
    return [
      this.statusClass(),
      this.sizeClass(),
      this.rootClass()
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}
